import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  Checkbox,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AssignmentIcon from "@mui/icons-material/Assignment";
import { useSelector } from "react-redux";
import axios from "axios";
import { baseURL } from "services/API";
import { useRentals, useOrganisations, useRelationshipManagers } from "services/hooks/rentals";

const adminBase = `${baseURL}/admin`;

const RelationshipManagers = () => {
  const token = useSelector((state) => state.token);
  const role = useSelector((state) => state.role);
  const { rentals } = useRentals();
  const { organisations, isLoadingOrganisations } = useOrganisations();
  const { relationshipManagers, isLoadingRelationshipManagers, error, setNewData } = useRelationshipManagers();
  const [message, setMessage] = useState({ type: "", text: "" });
  const [createOpen, setCreateOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assigningRmId, setAssigningRmId] = useState(null);
  const [selectedPropertyIds, setSelectedPropertyIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    username: "",
    password: "",
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    organisation_id: "",
  });

  const list = Array.isArray(relationshipManagers) ? relationshipManagers : [];
  const loading = isLoadingRelationshipManagers;
  const organisationsList = role === "admin" ? (Array.isArray(organisations) ? organisations : []) : [];
  const errorMessage = error ? "Failed to load relationship managers" : null;

  const openCreate = () => {
    setForm({
      username: "",
      password: "",
      first_name: "",
      last_name: "",
      email: "",
      phone_number: "",
      organisation_id: "",
    });
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    if (!form.username?.trim() || !form.password || !form.first_name?.trim() || !form.last_name?.trim()) {
      setMessage({ type: "error", text: "Username, password, first name and last name are required" });
      return;
    }
    setSubmitting(true);
    setMessage({ type: "", text: "" });
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const payload = {
        username: form.username.trim(),
        password: form.password,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email?.trim() || undefined,
        phone_number: form.phone_number?.trim() || undefined,
      };
      if (form.organisation_id) payload.organisation_id = Number(form.organisation_id);
      await axios.post(`${adminBase}/relationship_managers`, payload, { headers });
      setMessage({ type: "success", text: "Relationship Manager created" });
      setCreateOpen(false);
      setNewData(true);
    } catch (e) {
      const err = e?.response?.data?.error || e?.response?.data?.errors || e?.message;
      setMessage({ type: "error", text: typeof err === "object" ? JSON.stringify(err) : err });
    } finally {
      setSubmitting(false);
    }
  };

  const openAssign = (rm) => {
    setAssigningRmId(rm.id);
    setSelectedPropertyIds(rm.assigned_property_ids || []);
    setAssignOpen(true);
  };

  const toggleProperty = (id) => {
    setSelectedPropertyIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleAssignProperties = async () => {
    if (!assigningRmId) return;
    setSubmitting(true);
    setMessage({ type: "", text: "" });
    const headers = { Authorization: `Bearer ${token}` };
    try {
      await axios.post(
        `${adminBase}/relationship_managers/${assigningRmId}/assign_properties`,
        { property_ids: selectedPropertyIds },
        { headers }
      );
      setMessage({ type: "success", text: "Properties assigned" });
      setAssignOpen(false);
      setAssigningRmId(null);
      setNewData(true);
    } catch (e) {
      setMessage({ type: "error", text: e?.response?.data?.error || e?.message || "Assign failed" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm("Deactivate this Relationship Manager?")) return;
    const headers = { Authorization: `Bearer ${token}` };
    try {
      await axios.delete(`${adminBase}/relationship_managers/${id}`, { headers });
      setMessage({ type: "success", text: "Relationship Manager deactivated" });
      setNewData(true);
    } catch (e) {
      setMessage({ type: "error", text: e?.response?.data?.error || e?.message || "Deactivate failed" });
    }
  };

  const isAdmin = role === "admin";
  const isManager = role === "manager";
  if (!isAdmin && !isManager) {
    return (
      <Box m={2}>
        <Alert severity="warning">Only Admin or Property Manager can access Relationship Managers.</Alert>
      </Box>
    );
  }

  const propertiesForAssign = Array.isArray(rentals) ? rentals : [];

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">Relationship Managers</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          Add Relationship Manager
        </Button>
      </Box>

      {message.text && (
        <Alert
          severity={message.type === "error" ? "error" : "success"}
          sx={{ mb: 2 }}
          onClose={() => setMessage({ type: "", text: "" })}
        >
          {message.text}
        </Alert>
      )}

      <TableContainer component={Paper}>
        {loading ? (
          <Box p={4} display="flex" justifyContent="center">
            <CircularProgress />
          </Box>
        ) : errorMessage ? (
          <Alert severity="error">{errorMessage}</Alert>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Username</TableCell>
                <TableCell>Organisation</TableCell>
                <TableCell>Assigned Properties</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.id}</TableCell>
                  <TableCell>
                    {row.related_user
                      ? `${row.related_user.first_name || ""} ${row.related_user.last_name || ""}`.trim() || "—"
                      : "—"}
                  </TableCell>
                  <TableCell>{row.related_user?.username ?? "—"}</TableCell>
                  <TableCell>{row.related_organization ?? "—"}</TableCell>
                  <TableCell>
                    {Array.isArray(row.assigned_property_ids)
                      ? row.assigned_property_ids.length
                      : 0}{" "}
                    property(ies)
                  </TableCell>
                  <TableCell>{row.status ? "Active" : "Inactive"}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" title="Assign properties" onClick={() => openAssign(row)}>
                      <AssignmentIcon />
                    </IconButton>
                    <Button size="small" color="error" onClick={() => handleDeactivate(row.id)}>
                      Deactivate
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Create RM dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Relationship Manager</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Username"
            value={form.username}
            onChange={(e) => setForm((p) => ({ ...p, username: e.target.value }))}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            type="password"
            label="Password"
            value={form.password}
            onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="First name"
            value={form.first_name}
            onChange={(e) => setForm((p) => ({ ...p, first_name: e.target.value }))}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Last name"
            value={form.last_name}
            onChange={(e) => setForm((p) => ({ ...p, last_name: e.target.value }))}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={form.email}
            onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Phone"
            value={form.phone_number}
            onChange={(e) => setForm((p) => ({ ...p, phone_number: e.target.value }))}
            margin="normal"
          />
          {isAdmin && (
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Organisation (optional)</InputLabel>
              <Select
                value={form.organisation_id}
                label="Organisation (optional)"
                onChange={(e) => setForm((p) => ({ ...p, organisation_id: e.target.value }))}
              >
                <MenuItem value="">Default / Manager's org</MenuItem>
                {organisationsList.map((org) => (
                  <MenuItem key={org.id} value={org.id}>
                    {org.name} ({org.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={submitting}>
            {submitting ? <CircularProgress size={24} /> : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign properties dialog */}
      <Dialog open={assignOpen} onClose={() => { setAssignOpen(false); setAssigningRmId(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>Assign properties to RM</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Select properties to assign. Backend will enforce same organisation.
          </Typography>
          <List dense sx={{ maxHeight: 400, overflow: "auto", border: "1px solid #eee", borderRadius: 1 }}>
            {propertiesForAssign.map((r) => (
              <ListItem key={r.id}>
                <Checkbox
                  checked={selectedPropertyIds.includes(r.id)}
                  onChange={() => toggleProperty(r.id)}
                />
                <ListItemText primary={r.rental_name ?? r.name ?? `Property #${r.id}`} secondary={`ID: ${r.id}`} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAssignOpen(false); setAssigningRmId(null); }}>Cancel</Button>
          <Button variant="contained" onClick={handleAssignProperties} disabled={submitting}>
            {submitting ? <CircularProgress size={24} /> : "Save assignment"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RelationshipManagers;
